// Custom. Ref: https://shadcnui-expansions.typeart.cc/docs/multiple-selector
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { autoUpdate, computePosition, flip, offset, shift, size } from '@floating-ui/dom';
import { Cross1Icon, Cross2Icon, DragHandleDots2Icon } from '@radix-ui/react-icons';
import { Command as CommandPrimitive, useCommandState } from 'cmdk';
import * as React from 'react';
import { forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { Badge } from '../../primitives/badge';
import { Button } from '../../primitives/button';
import { Command, CommandGroup, CommandItem, CommandList } from '../../primitives/command';
import { LoadingAddon } from '../../primitives/loading-addon';
import { cn } from '../../utils/cn';
import { OptionInfoField, OptionInfoPopover } from '../option-info-popover';

export interface Option {
  value: string;
  label: string;
  disable?: boolean;
  infoFields?: OptionInfoField[];
  /** fixed option that can't be removed. */
  fixed?: boolean;
  /** Group the options by providing key. */
  [key: string]: string | boolean | OptionInfoField[] | undefined;
}
interface GroupOption {
  [key: string]: Option[];
}

interface MultipleSelectorProps {
  renderOption?: (option: Option) => React.ReactNode;
  value?: Array<Option | string | number>;
  defaultOptions?: Option[];
  /** manually controlled options */
  options?: Option[];
  placeholder?: string;
  /** Loading component. */
  loadingIndicator?: React.ReactNode;
  /** Empty component. */
  emptyIndicator?: React.ReactNode;
  /** Debounce time for async search. Only work with `onSearch`. */
  delay?: number;
  /**
   * Only work with `onSearch` prop. Trigger search when `onFocus`.
   * For example, when user click on the input, it will trigger the search to get initial options.
   **/
  triggerSearchOnFocus?: boolean;
  /** async search */
  onSearch?: (value: string) => Promise<Option[]>;
  /**
   * sync search. This search will not showing loadingIndicator.
   * The rest props are the same as async search.
   * i.e.: creatable, groupBy, delay.
   **/
  onSearchSync?: (value: string) => Option[];
  onChange?: (options: Option[]) => void;
  /** Limit the maximum number of selected options. */
  maxSelected?: number;
  /** When the number of selected options exceeds the limit, the onMaxSelected will be called. */
  onMaxSelected?: (maxLimit: number) => void;
  /** Hide the placeholder when there are options selected. */
  hidePlaceholderWhenSelected?: boolean;
  disabled?: boolean;
  isLoadingSx?: boolean;
  /** Group the options base on provided key. */
  groupBy?: string;
  className?: string;
  badgeClassName?: string;
  /**
   * First item selected is a default behavior by cmdk. That is why the default is true.
   * This is a workaround solution by add a dummy item.
   *
   * @reference: https://github.com/pacocoursey/cmdk/issues/171
   */
  selectFirstItem?: boolean;
  /** Allow user to create option when there is no option matched. */
  creatable?: boolean;
  /** Props of `Command` */
  commandProps?: React.ComponentPropsWithoutRef<typeof Command>;
  /** Props of `CommandInput` */
  inputProps?: Omit<
    React.ComponentPropsWithoutRef<typeof CommandPrimitive.Input>,
    'value' | 'placeholder' | 'disabled'
  >;
  /** hide the clear all button. */
  hideClearAllButton?: boolean;
}

export interface MultipleSelectorRef {
  selectedValue: Option[];
  input: HTMLInputElement;
  focus: VoidFunction;
  reset: VoidFunction;
}

function toOptionLike(item: unknown): Option | null {
  if (typeof item === 'string' || typeof item === 'number') {
    const normalized = String(item);
    return normalized ? { value: normalized, label: normalized } : null;
  }

  if (item && typeof item === 'object' && 'value' in item) {
    const value = String((item as { value: unknown }).value ?? '');
    if (!value) {
      return null;
    }

    const label = (item as { label?: unknown }).label;

    return {
      ...(item as Option),
      value,
      label: typeof label === 'string' && label ? label : value,
    };
  }

  return null;
}

function normalizeSelectedValue(
  value: MultipleSelectorProps['value'],
  availableOptions: Option[],
): Option[] {
  if (!Array.isArray(value) || value.length === 0) {
    return [];
  }

  const optionByValue = new Map(availableOptions.map((option) => [String(option.value), option]));
  const seenValues = new Set<string>();

  return value
    .map(toOptionLike)
    .filter((item): item is Option => Boolean(item))
    .filter((item) => {
      if (seenValues.has(item.value)) {
        return false;
      }

      seenValues.add(item.value);
      return true;
    })
    .map((item) => {
      const enriched = optionByValue.get(item.value);
      return enriched ? { ...item, ...enriched } : item;
    });
}

function areSelectedOptionsEqual(current: Option[], next: Option[]): boolean {
  if (current.length !== next.length) {
    return false;
  }

  return current.every((item, index) => {
    const candidate = next[index];

    return (
      item.value === candidate?.value &&
      item.label === candidate?.label &&
      item.fixed === candidate?.fixed &&
      item.disable === candidate?.disable &&
      JSON.stringify(item.infoFields ?? []) === JSON.stringify(candidate?.infoFields ?? [])
    );
  });
}

export function useDebounce<T>(value: T, delay?: number): T {
  const [debouncedValue, setDebouncedValue] = React.useState<T>(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
}

function transToGroupOption(options: Option[], groupBy?: string) {
  if (options.length === 0) {
    return {};
  }
  if (!groupBy) {
    return {
      '': options,
    };
  }

  const groupOption: GroupOption = {};
  options.forEach((option) => {
    const key = (option[groupBy] as string) || '';
    if (!groupOption[key]) {
      groupOption[key] = [];
    }
    groupOption[key].push(option);
  });
  return groupOption;
}

function removePickedOption(groupOption: GroupOption, picked: Option[]) {
  const cloneOption = JSON.parse(JSON.stringify(groupOption)) as GroupOption;

  for (const [key, value] of Object.entries(cloneOption)) {
    cloneOption[key] = value.filter((val) => !picked.find((p) => p.value === val.value));
  }
  return cloneOption;
}

function isOptionsExist(groupOption: GroupOption, targetOption: Option[]) {
  for (const [, value] of Object.entries(groupOption)) {
    if (value.some((option) => targetOption.find((p) => p.value === option.value))) {
      return true;
    }
  }
  return false;
}

/**
 * The `CommandEmpty` of shadcn/ui will cause the cmdk empty not rendering correctly.
 * So we create one and copy the `Empty` implementation from `cmdk`.
 *
 * @reference: https://github.com/hsuanyi-chou/shadcn-ui-expansions/issues/34#issuecomment-1949561607
 **/
const CommandEmpty = forwardRef<
  HTMLDivElement,
  React.ComponentProps<typeof CommandPrimitive.Empty>
>(({ className, ...props }, forwardedRef) => {
  const render = useCommandState((state) => state.filtered.count === 0);

  if (!render) return null;

  return (
    <div
      ref={forwardedRef}
      className={cn('py-6 text-center text-sm', className)}
      cmdk-empty=""
      role="presentation"
      {...props}
    />
  );
});

CommandEmpty.displayName = 'CommandEmpty';

interface SortableSelectedBadgeProps {
  renderOption?: MultipleSelectorProps['renderOption'];
  option: Option;
  badgeClassName?: string;
  disabled?: boolean;
  onUnselect: (option: Option) => void;
}

function SortableSelectedBadge({
  option,
  badgeClassName,
  disabled,
  onUnselect,
  renderOption,
}: SortableSelectedBadgeProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: option.value,
    disabled,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 1 : undefined,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Badge
        className={cn(
          'data-[disabled]:bg-muted-foreground data-[disabled]:text-muted data-[disabled]:hover:bg-muted-foreground',
          'data-[fixed]:bg-muted-foreground data-[fixed]:text-muted data-[fixed]:hover:bg-muted-foreground',
          'min-h-7 bg-secondary text-xs text-secondary-foreground shadow-none',
          badgeClassName,
        )}
        data-fixed={option.fixed}
        data-disabled={disabled || undefined}
      >
        <Button
          type="button"
          size="icon-sm"
          variant="ghost"
          aria-label="Arrastrar etiqueta"
          title="Arrastrar etiqueta"
          className={cn('mr-2 size-4 flex-none cursor-grab touch-none p-0', disabled && 'hidden')}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          {...attributes}
          {...listeners}
        >
          <DragHandleDots2Icon className="size-3! text-muted-foreground" />
        </Button>
        <span className="font-regular inline-flex items-center break-all">
          {renderOption ? renderOption(option) : option.label}
          <OptionInfoPopover fields={option.infoFields} />
        </span>
        <button
          className={cn(
            'ml-2 rounded-full ring-offset-background outline-hidden focus:ring-1 focus:ring-ring focus:ring-offset-2',
            (disabled || option.fixed) && 'hidden',
          )}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              onUnselect(option);
            }
          }}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onClick={() => onUnselect(option)}
        >
          <Cross1Icon className="size-3! text-muted-foreground hover:text-foreground" />
        </button>
      </Badge>
    </div>
  );
}

const MultiSelector = React.forwardRef<MultipleSelectorRef, MultipleSelectorProps>(
  (
    {
      value,
      onChange,
      placeholder,
      defaultOptions: arrayDefaultOptions = [],
      options: arrayOptions = [],
      delay,
      onSearch,
      onSearchSync,
      loadingIndicator,
      emptyIndicator,
      maxSelected = Number.MAX_SAFE_INTEGER,
      onMaxSelected,
      hidePlaceholderWhenSelected,
      disabled,
      isLoadingSx = false,
      groupBy,
      className,
      badgeClassName,
      renderOption,
      selectFirstItem = true,
      creatable = false,
      triggerSearchOnFocus = false,
      commandProps,
      inputProps,
      hideClearAllButton = false,
    }: MultipleSelectorProps,
    ref: React.Ref<MultipleSelectorRef>,
  ) => {
    // clean items with errors
    const newArrayOptions = React.useMemo(
      () => arrayOptions?.filter(({ value }) => value !== ''),
      [arrayOptions],
    );
    const newPlaceholder =
      arrayOptions?.length > 0 && newArrayOptions.length <= 0
        ? 'La fuente de datos no contiene valores válidos'
        : placeholder;
    const availableOptions = React.useMemo(
      () => [...arrayDefaultOptions, ...newArrayOptions],
      [arrayDefaultOptions, newArrayOptions],
    );

    const inputRef = React.useRef<HTMLInputElement>(null);
    const [open, setOpen] = React.useState(false);
    const [onScrollbar, setOnScrollbar] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const dropdownRef = React.useRef<HTMLDivElement>(null);
    const portalRef = React.useRef<HTMLDivElement>(null);
    const [dropdownPos, setDropdownPos] = React.useState<{
      top: number;
      left: number;
      width: number;
      maxHeight?: number;
    }>({ top: 0, left: 0, width: 0 });

    const [selected, setSelected] = React.useState<Option[]>(() =>
      normalizeSelectedValue(value, availableOptions),
    );
    const [options, setOptions] = React.useState<GroupOption>(
      transToGroupOption(arrayDefaultOptions, groupBy),
    );
    const [inputValue, setInputValue] = React.useState('');
    const debouncedSearchTerm = useDebounce(inputValue, delay || 500);
    const selectables = React.useMemo<GroupOption>(
      () => removePickedOption(options, selected),
      [options, selected],
    );
    const hasSelectableOptions = React.useMemo(
      () => Object.values(selectables).some((opts) => opts.length > 0),
      [selectables],
    );
    const selectedIds = React.useMemo(() => selected.map((option) => option.value), [selected]);
    const selectedBadgesSensors = useSensors(
      useSensor(PointerSensor),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates,
      }),
    );

    React.useImperativeHandle(
      ref,
      () => ({
        selectedValue: [...selected],
        input: inputRef.current as HTMLInputElement,
        focus: () => inputRef?.current?.focus(),
        reset: () => setSelected([]),
      }),
      [selected],
    );

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node) &&
        (!portalRef.current || !portalRef.current.contains(event.target as Node))
      ) {
        setOpen(false);
        inputRef.current.blur();
      }
    };

    const handleUnselect = React.useCallback(
      (option: Option) => {
        const newOptions = selected.filter((s) => s.value !== option.value);
        setSelected(newOptions);
        onChange?.(newOptions);
      },
      [onChange, selected],
    );

    const handleSelectedBadgesDragEnd = React.useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        setSelected((prev) => {
          const oldIndex = prev.findIndex((option) => option.value === active.id);
          const newIndex = prev.findIndex((option) => option.value === over.id);

          if (oldIndex < 0 || newIndex < 0) {
            return prev;
          }

          const reordered = arrayMove(prev, oldIndex, newIndex);
          onChange?.(reordered);
          return reordered;
        });
      },
      [onChange],
    );

    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLDivElement>) => {
        const input = inputRef.current;
        if (input) {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            if (input.value === '' && selected.length > 0) {
              const lastSelectOption = selected[selected.length - 1];
              // If last item is fixed, we should not remove it.
              if (!lastSelectOption.fixed) {
                handleUnselect(selected[selected.length - 1]);
              }
            }
          }
          // This is not a default behavior of the <input /> field
          if (e.key === 'Escape') {
            input.blur();
          }
        }
      },
      [handleUnselect, selected],
    );

    useEffect(() => {
      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchend', handleClickOutside);
      } else {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchend', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchend', handleClickOutside);
      };
    }, [open]);

    useEffect(() => {
      if (!open || !dropdownRef.current || !portalRef.current) return;

      const reference = dropdownRef.current;
      const floating = portalRef.current;

      const cleanup = autoUpdate(reference, floating, () => {
        void computePosition(reference, floating, {
          placement: 'bottom-start',
          strategy: 'fixed',
          middleware: [
            offset(4),
            flip({ fallbackPlacements: ['top-start'] }),
            shift({ padding: 8 }),
            size({
              padding: 8,
              apply({ availableHeight, rects, x, y }) {
                setDropdownPos({
                  left: x,
                  top: y,
                  width: rects.reference.width,
                  maxHeight: Math.max(availableHeight, 0),
                });
              },
            }),
          ],
        });
      });

      return cleanup;
    }, [open, hasSelectableOptions, isLoading, inputValue, creatable]);

    useEffect(() => {
      if (Array.isArray(value)) {
        const normalizedSelected = normalizeSelectedValue(value, availableOptions);
        setSelected((prev) =>
          areSelectedOptionsEqual(prev, normalizedSelected) ? prev : normalizedSelected,
        );
      }
    }, [availableOptions, value]);

    useEffect(() => {
      /** If `onSearch` is provided, do not trigger options updated. */
      if (!newArrayOptions || onSearch) {
        return;
      }
      const newOption = transToGroupOption(newArrayOptions || [], groupBy);
      if (JSON.stringify(newOption) !== JSON.stringify(options)) {
        setOptions(newOption);
      }
    }, [arrayDefaultOptions, newArrayOptions, groupBy, onSearch, options]);

    useEffect(() => {
      /** sync search */

      const doSearchSync = () => {
        const res = onSearchSync?.(debouncedSearchTerm);
        setOptions(transToGroupOption(res || [], groupBy));
      };

      const exec = async () => {
        if (!onSearchSync || !open) return;

        if (triggerSearchOnFocus) {
          doSearchSync();
        }

        if (debouncedSearchTerm) {
          doSearchSync();
        }
      };

      void exec();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm, groupBy, open, triggerSearchOnFocus]);

    useEffect(() => {
      /** async search */

      const doSearch = async () => {
        setIsLoading(true);
        const res = await onSearch?.(debouncedSearchTerm);
        setOptions(transToGroupOption(res || [], groupBy));
        setIsLoading(false);
      };

      const exec = async () => {
        if (!onSearch || !open) return;

        if (triggerSearchOnFocus) {
          await doSearch();
        }

        if (debouncedSearchTerm) {
          await doSearch();
        }
      };

      void exec();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearchTerm, groupBy, open, triggerSearchOnFocus]);

    const CreatableItem = () => {
      if (!creatable) return undefined;
      if (
        isOptionsExist(options, [{ value: inputValue, label: inputValue }]) ||
        selected.find((s) => s.value === inputValue)
      ) {
        return undefined;
      }

      const Item = (
        <CommandItem
          value={inputValue}
          className="cursor-pointer px-2 text-sm"
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
          onSelect={(value: string) => {
            if (selected.length >= maxSelected) {
              onMaxSelected?.(selected.length);
              return;
            }
            setInputValue('');
            const parts = value
              .split(',')
              .map((p) => p.trim())
              .filter(Boolean);
            const uniqueNewParts = parts.filter(
              (p) =>
                !selected.find((s) => s.value === p) &&
                !isOptionsExist(options, [{ value: p, label: p }]),
            );
            const slotsLeft = maxSelected - selected.length;
            const partsToAdd = uniqueNewParts.slice(0, slotsLeft);
            const newOptions = [...selected, ...partsToAdd.map((p) => ({ value: p, label: p }))];
            setSelected(newOptions);
            onChange?.(newOptions);
            if (partsToAdd.length < uniqueNewParts.length) {
              onMaxSelected?.(newOptions.length);
            }
          }}
        >
          {`Crear: ${inputValue}`}
        </CommandItem>
      );

      // For normal creatable
      if (!onSearch && inputValue.length > 0) {
        return Item;
      }

      // For async search creatable. avoid showing creatable item before loading at first.
      if (onSearch && debouncedSearchTerm.length > 0 && !isLoading) {
        return Item;
      }

      return undefined;
    };

    const EmptyItem = React.useCallback(() => {
      if (!emptyIndicator) return undefined;

      // For async search that showing emptyIndicator
      if (onSearch && !creatable && Object.keys(options).length === 0) {
        return (
          <CommandItem value="-" disabled>
            {emptyIndicator}
          </CommandItem>
        );
      }

      return <CommandEmpty className="p-0">{emptyIndicator}</CommandEmpty>;
    }, [creatable, emptyIndicator, onSearch, options]);

    /** Avoid Creatable Selector freezing or lagging when paste a long string. */
    const commandFilter = React.useCallback(() => {
      if (commandProps?.filter) {
        return commandProps.filter;
      }

      if (creatable) {
        return (value: string, search: string) => {
          return value.toLowerCase().includes(search.toLowerCase()) ? 1 : -1;
        };
      }
      // Using default filter in `cmdk`. We don't have to provide it.
      return undefined;
    }, [creatable, commandProps?.filter]);

    return (
      <Command
        ref={dropdownRef}
        {...commandProps}
        onKeyDown={(e) => {
          handleKeyDown(e);
          commandProps?.onKeyDown?.(e);
        }}
        className={cn('h-auto overflow-visible bg-transparent shadow-xs', commandProps?.className)}
        shouldFilter={
          commandProps?.shouldFilter !== undefined ? commandProps.shouldFilter : !onSearch
        } // When onSearch is provided, we don't want to filter the options. You can still override it.
        filter={commandFilter()}
      >
        <div
          className={cn(
            'relative min-h-9 rounded-lg border border-input text-sm ring-offset-background focus-within:ring-1 focus-within:ring-ring',
            {
              'p-1': selected.length !== 0,
              'cursor-text': !disabled && selected.length !== 0,
              'cursor-not-allowed border-input/50': disabled || isLoadingSx,
            },
            className,
          )}
          onClick={() => {
            if (disabled) return;
            inputRef?.current?.focus();
          }}
        >
          <div className="relative flex flex-wrap gap-1 pr-9">
            <DndContext
              sensors={selectedBadgesSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleSelectedBadgesDragEnd}
            >
              <SortableContext items={selectedIds} strategy={rectSortingStrategy}>
                {selected.map((option) => (
                  <SortableSelectedBadge
                    key={option.value}
                    option={option}
                    renderOption={renderOption}
                    badgeClassName={badgeClassName}
                    disabled={disabled}
                    onUnselect={handleUnselect}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {/* Avoid having the "Search" Icon */}
            <div
              id={inputProps?.id}
              className={cn(
                'flex-1 bg-transparent outline-hidden placeholder:text-muted-foreground',
                {
                  'w-full': hidePlaceholderWhenSelected,
                  'px-3 py-2': selected.length === 0,
                  'ml-1': selected.length !== 0,
                },
                inputProps?.className,
              )}
            >
              <CommandPrimitive.Input
                {...inputProps}
                ref={inputRef}
                value={inputValue}
                disabled={disabled}
                onValueChange={(value) => {
                  setInputValue(value);
                  inputProps?.onValueChange?.(value);
                }}
                onBlur={(event) => {
                  if (!onScrollbar) {
                    setOpen(false);
                  }
                  inputProps?.onBlur?.(event);
                }}
                onFocus={(event) => {
                  setOpen(true);
                  triggerSearchOnFocus && onSearch?.(debouncedSearchTerm);
                  inputProps?.onFocus?.(event);
                }}
                placeholder={
                  hidePlaceholderWhenSelected && selected.length !== 0 ? '' : newPlaceholder
                }
                className={cn(
                  'h-full w-full bg-transparent outline-hidden placeholder:text-muted-foreground',
                )}
              />
            </div>
            <Button
              type="button"
              size="icon"
              variant="secondary"
              onClick={() => {
                setSelected(selected.filter((s) => s.fixed));
                onChange?.(selected.filter((s) => s.fixed));
              }}
              className={cn(
                'absolute right-0 size-7 p-0',
                (hideClearAllButton ||
                  disabled ||
                  selected.length < 1 ||
                  selected.filter((s) => s.fixed).length === selected.length) &&
                  'hidden',
              )}
            >
              <Cross2Icon />
            </Button>
          </div>
          {isLoadingSx && <LoadingAddon type="plain" />}
        </div>
        {open &&
          (isLoading || hasSelectableOptions || (creatable && inputValue.length > 0)) &&
          createPortal(
            <div
              ref={portalRef}
              style={{
                position: 'fixed',
                top: dropdownPos.top,
                left: dropdownPos.left,
                width: dropdownPos.width,
                zIndex: 9999,
                pointerEvents: 'auto',
              }}
            >
              <CommandList
                className="pointer-events-auto animate-in rounded-lg border bg-popover text-popover-foreground shadow-md outline-hidden"
                style={{
                  maxHeight: dropdownPos.maxHeight,
                }}
                onMouseLeave={() => {
                  setOnScrollbar(false);
                }}
                onMouseEnter={() => {
                  setOnScrollbar(true);
                }}
                onMouseUp={() => {
                  inputRef?.current?.focus();
                }}
              >
                {isLoading ? (
                  // eslint-disable-next-line react/jsx-no-useless-fragment
                  <>{loadingIndicator}</>
                ) : (
                  <>
                    {EmptyItem()}
                    {CreatableItem()}
                    {!selectFirstItem && <CommandItem value="-" className="hidden" />}
                    {Object.entries(selectables).map(([key, dropdowns]) => (
                      <CommandGroup key={key} heading={key} className="h-full overflow-auto">
                        <>
                          {dropdowns.map((option) => {
                            return (
                              <CommandItem
                                key={option.value}
                                value={option.value}
                                disabled={option.disable}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                }}
                                onSelect={() => {
                                  if (selected.length >= maxSelected) {
                                    onMaxSelected?.(selected.length);
                                    return;
                                  }
                                  setInputValue('');
                                  const newOptions = [...selected, option];
                                  setSelected(newOptions);
                                  onChange?.(newOptions);
                                }}
                                className={cn(
                                  'cursor-pointer break-all',
                                  option.disable && 'cursor-default text-muted-foreground',
                                )}
                              >
                                <span
                                  className={cn('inline-flex items-center gap-1', {
                                    'w-full': Boolean(renderOption),
                                  })}
                                >
                                  {renderOption ? renderOption(option) : option.label}
                                  <OptionInfoPopover fields={option.infoFields} />
                                </span>
                              </CommandItem>
                            );
                          })}
                        </>
                      </CommandGroup>
                    ))}
                  </>
                )}
              </CommandList>
            </div>,
            document.body,
          )}
      </Command>
    );
  },
);

MultiSelector.displayName = 'MultiSelector';
export { MultiSelector };
